// Shared utilities for top-level eager ds-first transform ops
// (`columns`, `distinct`, `rename`, `fold`, `spread`, `unroll`, …).
// Every op of the shape `(<name> ds …)` runs the same prefix —
// type-check the first arg as a Dataset, materialize a LazyDataset,
// validate column names, project rows back into columns — and shares
// the `:limit` schema for fan-out-bounded reshape ops.

import type { ColumnTable } from 'arquero';
import { z } from 'zod';

import type { SourcePos } from '../../ast.ts';
import { MixedColumnKindError, UnknownColumnRefError } from '../../errors.ts';
import { astShape, embeddedString, nodePos, opCallPos } from '../../normalize.ts';
import {
  type ColumnRefInArg,
  type OpExample,
  opMeta,
  type PropagateSchemaCtx,
  type SpecialCtx,
} from '../../op-meta.ts';
import { elemPath, paramPath } from '../../path.ts';
import type { PlanNode } from '../../plan.ts';
import { withTable } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { type ColumnName, ColumnNameSchema, columnNameString } from '../../schema/keyword.ts';
import { nestedDatasetCol, type Schema, schemaFromEntries } from '../../schema/types.ts';
import {
  type Cell,
  type Columns,
  Dataset,
  expectDataset,
  isLazy,
  LazyDataset,
  MaterializedDataset,
  type Value,
} from '../../value.ts';

/**
 * Resolve a `Dataset` argument to a `MaterializedDataset`:
 *
 *   1. `expectDataset` — `TypeMismatchError` if the arg isn't a Dataset,
 *      using `opLabel` (e.g., `'(rename ds :old :new)'`) in the message.
 *   2. `ctx.materialize` — force a `LazyDataset`. A
 *      `MaterializedDataset` is returned as-is.
 *
 * The error position defaults to the op call as a whole — same
 * convention `expectDataset` callers use elsewhere in the engine.
 * Multi-ds ops (e.g. `join`) pass an explicit `pos` to blame the
 * specific positional that wasn't a Dataset.
 */
export const resolveDsArg = async (
  rawArg: unknown,
  opLabel: string,
  ctx: SpecialCtx,
  pos?: SourcePos,
): Promise<MaterializedDataset> => {
  const errorPos = pos ?? opCallPos(ctx.spans, ctx.path);
  const dsValue = expectDataset(rawArg as Value, opLabel, errorPos);
  return isLazy(dsValue) ? await ctx.materialize(dsValue) : (dsValue as MaterializedDataset);
};
/**
 * SSOT for inner-cell materialisation: forces a `LazyDataset` cell
 * (i.e. a `Dataset` carrying a deferred plan) to its concrete
 * `MaterializedDataset` via the engine's `ctx.materialize`, and
 * short-circuits already-materialised cells to `Promise.resolve(cell)`.
 *
 * Used wherever an op walks per-cell over a nested-dataset column —
 * `unnest`, `time-bucket` per-cell form. Both call this from inside
 * a `Promise.all` over rows so the `ts-ref` fetcher's microtask
 * batcher coalesces all cell fetches into a single CDF round-trip.
 */
export const materializeCell = (cell: Dataset, ctx: SpecialCtx): Promise<MaterializedDataset> =>
  isLazy(cell) ? cell.materialize(ctx) : Promise.resolve(cell as MaterializedDataset);
/**
 * Per-cell dispatch helper for top-level eager ops that operate on a
 * nested-dataset (TS-ref) column row-by-row. For each `spec.col` and
 * each row's cell:
 *
 *   - **Lazy cell** → return a new `LazyDataset` whose plan extends the
 *     cell's plan with the nodes returned by
 *     `spec.cellPlanNodes(innerSchemaBefore)` and whose declared
 *     schema is `spec.innerSchemaAfter`. The op(s) are recorded on the
 *     cell's plan and run at materialisation time via the standard
 *     replay path — preserving the substrate invariant "lazy cells
 *     stay lazy through per-cell lift". A provider's `adaptPlan`
 *     (e.g. the timeseries provider absorbing a per-cell `time-bucket`)
 *     turns the recorded node(s) into push-down hints at fetch time. The
 *     factory returns a list so a
 *     single per-cell op can record an atomic *chain* (e.g. per-cell
 *     time-bucket records `time-bucket` + `rename` to project the
 *     bucket col name to the per-cell convention).
 *   - **Materialised cell** → run `spec.cellFn(innerDs)` eagerly. The
 *     cell is already concrete data; there's no lazy plan to extend.
 *   - **Null / non-`Dataset` cell** → passes through as `null`,
 *     semantically "no series exists for this row" (engine-wide
 *     convention, matching the guard in `unnest`/`unnestZip`). Neither
 *     branch runs on null cells; an empty `Dataset` cell (schema-
 *     bearing, zero rows) DOES flow through.
 *
 * The lazy-vs-materialised split unifies two per-cell mechanisms — the
 * substrate's generic auto-lift (which records plan nodes onto lazy
 * cells) and op-internal eager iteration. Forcing materialisation
 * immediately would break ts-ref push-down, so this helper records onto
 * the cell's plan when it can and runs eagerly only when it must. The
 * outer dataset is rebuilt
 * with each lifted col's type rewritten to
 * `nested-dataset(innerSchemaAfter)`.
 *
 * Caller specifies each lifted col's `innerSchemaAfter` up front (not
 * derived from produced cells), so zero-row outer datasets still
 * rewrite to the correct outer schema. `cellPlanNodes` is a factory of
 * the cell's pre-op inner schema so a single helper handles both raw
 * TS-ref cells and (e.g.) already-reduced cells whose inner schema
 * differs.
 *
 * Precondition: every `spec.col` exists on `ds.schema` with kind
 * `nested-dataset`. The dispatching op is responsible for that
 * validation; this helper is a pure mechanism and does not re-check.
 */
export const liftPerCellOnColumns = async (
  ds: MaterializedDataset,
  specs: readonly {
    readonly col: string;
    readonly innerSchemaAfter: Schema;
    readonly cellPlanNodes: (innerSchemaBefore: Schema) => readonly PlanNode[];
    readonly cellFn: (innerDs: MaterializedDataset) => Promise<MaterializedDataset>;
  }[],
  _ctx: SpecialCtx,
): Promise<MaterializedDataset> => {
  const liftedEntries = await Promise.all(
    specs.map(async ({ col, innerSchemaAfter, cellPlanNodes, cellFn }): Promise<readonly [string, Cell[]]> => {
      const cells = ds.columns[col] ?? [];
      const out = await Promise.all(
        cells.map(async (cell): Promise<Cell> => {
          if (!(cell instanceof Dataset)) {
            return null;
          }
          if (isLazy(cell)) {
            const nodes = cellPlanNodes(cell.schema);
            return new LazyDataset({ source: cell.plan.source, ops: [...cell.plan.ops, ...nodes] }, innerSchemaAfter);
          }
          return cellFn(cell as MaterializedDataset);
        }),
      );
      return [col, out];
    }),
  );
  const liftedByName = new Map(liftedEntries);
  const innerByCol = new Map(specs.map(({ col, innerSchemaAfter }) => [col, innerSchemaAfter] as const));
  const newSchema = schemaFromEntries(
    Array.from(ds.schema.entries()).map(
      ([k, t]) =>
        (innerByCol.has(k) ? [k, nestedDatasetCol(innerByCol.get(k) as Schema)] : [k, t]) as readonly [
          string,
          typeof t,
        ],
    ),
  );
  const newColumns: Columns = Object.fromEntries(
    Array.from(ds.schema.keys()).map((name) => [name, liftedByName.get(name) ?? ds.columns[name] ?? []]),
  );
  // Per-cell lift rewrites nested-dataset columns but preserves the OUTER
  // row set and order, so the outer identity axis passes through unchanged.
  return new MaterializedDataset(newColumns, newSchema, ds.identity);
};
/**
 * Drop the named synthetic columns from `dataset`. No-op when
 * `synthetics` is empty. Used by every lift site after a verb's
 * arquero call to ensure synthetic columns never reach downstream
 * consumers.
 */
export const dropSynthetic = (dataset: MaterializedDataset, synthetics: readonly string[]): MaterializedDataset => {
  if (synthetics.length === 0) {
    return dataset;
  }
  return withTable(dataset, (t: ColumnTable) =>
    t.select(...t.columnNames().filter((c: string) => !synthetics.includes(c))),
  );
};

// ## column-name validation

/**
 * Throw `UnknownColumnRefError` on the first `names` entry that isn't
 * present on `schema`. `posOf(idx)` computes the source position to
 * blame for the missing name at index `idx` — each caller closes over
 * its own arg layout (positional, vector element, kwarg, …). No-op
 * when every name is present.
 *
 * The error's `available` field is the schema's column list — this is
 * what surfaces as the "did you mean" in the diagnostic.
 */
export const requireColumns = (names: readonly string[], schema: Schema, posOf: (idx: number) => SourcePos): void => {
  const missingIdx = names.findIndex((n) => !schema.has(n));
  if (missingIdx === -1) {
    return;
  }
  const missing = names[missingIdx] as string;
  throw new UnknownColumnRefError(missing, Array.from(schema.keys()), posOf(missingIdx));
};

/**
 * Auto-dispatch classifier for per-cell (nested-dataset / TS-ref) vs plain
 * column ops. Returns `true` when any of `cols` is a `nested-dataset` column
 * on `schema`, signalling the per-cell path; `false` for the plain path.
 *
 * Mixing nested-dataset and plain columns in a single call is rejected with a
 * `MixedColumnKindError` at `pos` — its `suggestions()` names which listed
 * columns are TS-ref vs plain and the split recipe, surfacing the kind
 * divergence the auto-dispatch syntax hides. `label` names the op in the
 * message (interpolated, so window ops carry their own opName); `plainLabel`
 * names the non-nested column class (`plain` for window, `plain-timestamp`
 * for time-bucket).
 */
export const classifyPerCell = (
  cols: readonly string[],
  schema: Schema,
  label: string,
  plainLabel: string,
  pos: SourcePos,
): boolean => {
  const isNested = (c: string) => schema.get(c)?.kind === 'nested-dataset';
  const tsRefCols = cols.filter(isNested);
  const plainCols = cols.filter((c) => !isNested(c));
  if (tsRefCols.length > 0 && plainCols.length > 0) {
    throw new MixedColumnKindError(label, plainLabel, tsRefCols, plainCols, pos);
  }
  return tsRefCols.length > 0;
};

// ## row-major → column-major

/**
 * Transpose `rows` (one record per output row, keyed by column name)
 * into `cols` (one entry per column, value-list aligned by row index).
 * A missing key in a row contributes `null` to its column — defensive
 * default for callers that conditionally set fields per row.
 */
export const rowsToColumns = (
  rows: readonly Record<string, unknown>[],
  cols: readonly string[],
): Record<string, Cell[]> => Object.fromEntries(cols.map((c) => [c, rows.map((r) => (r[c] ?? null) as Cell)]));

// ## reshape `:limit`

/** Default `:limit` cap for `spread` / `unroll` fan-out. */
export const DEFAULT_RESHAPE_LIMIT = 100;
/** Hard upper bound on `:limit` — guards against runaway output. */
export const MAX_RESHAPE_LIMIT = 10000;

/** Optional non-negative integer `:limit`, capped at MAX_RESHAPE_LIMIT. */
export const LimitSchema = z.coerce.number().int().nonnegative().max(MAX_RESHAPE_LIMIT).optional();

// ## ds-column-list op builder

/**
 * Extract the column-name string from a `ColumnName`-shaped AST node
 * (`{$kw: name}` or a quoted string literal). Returns `undefined` for
 * malformed shapes — `referencedColumns` / `propagateSchema` stay
 * tolerant; apply-time validation throws on the same data. SSOT for
 * "decode a positional `ColumnName` arg without throwing", shared by
 * `buildDsColumnListOp` and any other op that takes a `ColumnName`
 * positional and needs to enumerate it in `referencedColumns` /
 * `propagateSchema`.
 */
export const columnNameAstName = (node: unknown): string | undefined => {
  const shape = astShape(node);
  if (shape.kind === 'keyword') {
    return shape.name;
  }
  return embeddedString(node);
};

/**
 * Map a vector-AST whose elements are `ColumnName`-shaped into a list
 * of `ColumnRefInArg` records (name + spans-path). SSOT for the pattern
 * "treat each element of a `[:a :b :c]` arg as a column USAGE", shared
 * by `pivot`, `aggregate`, `time-bucket`, `join`, etc. Malformed
 * elements are skipped (positional indices for valid ones still align
 * with their source position via `elemPath`).
 */
export const vectorColRefs = (node: unknown, basePath: string): readonly ColumnRefInArg[] => {
  const shape = astShape(node);
  if (shape.kind !== 'vector') {
    return [];
  }
  return shape.elements.flatMap((el, i) => {
    const name = columnNameAstName(el);
    return name === undefined ? [] : [{ name, path: elemPath(basePath, i) }];
  });
};
/**
 * Map a single `ColumnName`-shaped arg at `params[index]` to a one-element
 * `ColumnRefInArg` list (empty when the arg is absent or malformed). The scalar
 * sibling of {@link vectorColRefs}, shared by special-form ops that read a
 * single positional `:column` and must report it in `referencedColumns`.
 */
export const singleColRefAt = (data: unknown, basePath: string, index: number): readonly ColumnRefInArg[] => {
  const node = (data as { readonly params?: readonly unknown[] }).params?.[index];
  const name = columnNameAstName(node);
  return name === undefined ? [] : [{ name, path: paramPath(basePath, index) }];
};

/**
 * Decode a vector-AST whose elements are `ColumnName`-shaped into a
 * flat list of names, skipping malformed entries. Returns `[]` when
 * `node` isn't a vector. SSOT for the `[:a :b :c]` form used by
 * `fold`'s melt-cols, `unnest`'s multi-col selector, etc.
 */
export const columnNameAstList = (node: unknown): readonly string[] => {
  const shape = astShape(node);
  if (shape.kind !== 'vector') {
    return [];
  }
  return shape.elements.flatMap((el) => {
    const name = columnNameAstName(el);
    return name === undefined ? [] : [name];
  });
};

/**
 * Build a top-level eager op of the shape `(<name> ds [:a :b …])` —
 * `Dataset` followed by a single non-empty vector of `ColumnName`s —
 * sharing the dispatch flow used by `columns` and `distinct`:
 *
 *   1. Force a `LazyDataset` input via `ctx.materialize`.
 *   2. Normalise each `ColumnName` (keyword | string) to its string form.
 *   3. First-miss validation: throw `UnknownColumnRefError` (with the
 *      dataset's columns as the "did you mean") on the first listed
 *      name that isn't on `ds.schema`.
 *   4. Hand the materialised `Dataset` + the resolved string names to
 *      the per-op `verb` callback. The verb owns its own tactic —
 *      arquero round-trip, direct column projection, structural
 *      partition, anything that fits.
 *
 * The vector form is the SSOT for "name a list of columns": identical
 * shape to the `(columns [:a :b])` projection form inside a
 * `(query "V" …)` source pipeline.
 *
 * Per-op specifics — `verb`, `propagateSchema`, `example`, `describe`,
 * and the literal `name` — are the only inputs; everything else (Zod
 * shape, validation, error shape, materialization, `referencedColumns`)
 * is shared.
 */
export const buildDsColumnListOp = <NameType extends string>({
  name,
  describe,
  example,
  verb,
  propagateSchema,
}: {
  readonly name: NameType;
  readonly describe: string;
  readonly example: OpExample;
  readonly verb: (ds: MaterializedDataset, names: readonly string[]) => MaterializedDataset;
  readonly propagateSchema: (names: readonly string[], ctx: PropagateSchemaCtx) => Schema;
}) =>
  z
    .object({
      name: z.literal(name),
      // [Dataset, [ColumnName, …]] — single non-empty vector arg.
      params: z.tuple([astArg('<dataset>'), z.array(ColumnNameSchema).min(1)]),
    })
    .describe(describe)
    .register(opMeta, {
      evaluation: 'eager',
      effect: 'pure',
      example,
      apply: async (data, ctx: SpecialCtx): Promise<MaterializedDataset> => {
        const [dsRaw, namesRaw] = data.params as readonly [Value, readonly ColumnName[]];
        const concrete = await resolveDsArg(dsRaw, `(${name} ds [:a :b …])`, ctx);
        const names = namesRaw.map(columnNameString);
        requireColumns(names, concrete.schema, (idx) => nodePos(ctx.spans, elemPath(paramPath(ctx.path, 1), idx)));
        return verb(concrete, names);
      },
      // Names live in the vector AST at params[1]; `columnNameAstList`
      // and `vectorColRefs` are the SSOT decoders shared with every
      // other vector-of-column-names slot (fold, the `(columns …)`
      // projection form, …).
      dataset: {
        propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
          const namesAst = (data as { readonly params?: readonly unknown[] }).params?.[1];
          return propagateSchema(columnNameAstList(namesAst), ctx);
        },
        referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
          const namesAst = (data as { readonly params?: readonly unknown[] }).params?.[1];
          return vectorColRefs(namesAst, paramPath(basePath, 1));
        },
      },
    });
